import type {
  ContainerNode,
  EntityScope,
  Fill,
  FrozenPage,
  Node,
  NodeId,
  PageBlueprint,
  TemplateVersion,
} from '@menuvera/engine';
import { isFrozenDesign } from '@menuvera/engine';
import { current, isDraft } from 'immer';

/**
 * The authored part of a template version.
 *
 * Identity and status are derived from the database row, so the editor never
 * sees them and cannot get them out of step with the record.
 */
export type TemplateDocument = Omit<TemplateVersion, 'id' | 'templateId' | 'version' | 'status'> & {
  /** 2 = frame-based spatial canvas. Migrated on load when missing or < 2. */
  spatialVersion?: number;
};

export function isFrozenDocument(
  document: TemplateDocument | null | undefined,
): document is TemplateDocument & { mode: 'frozen'; pages: FrozenPage[] } {
  return isFrozenDesign(document);
}

/** Synthetic blueprints so the shared editor can edit frozen page roots. */
export function frozenPagesAsBlueprints(pages: FrozenPage[]): PageBlueprint[] {
  return pages.map((page, index) => ({
    id: page.id,
    group: page.group,
    label: page.title?.trim() || `Page ${index + 1}`,
    variant: page.id,
    constraints: {},
    weight: 10,
    root: page.root,
  }));
}

export function blueprintsForEditor(
  document: TemplateDocument | null | undefined,
  variantId?: string | null,
): PageBlueprint[] {
  if (!document) return [];
  if (isFrozenDocument(document)) {
    return frozenPagesAsBlueprints(document.pages);
  }
  return document.variants?.find((variant) => variant.id === variantId)?.blueprints ??
    document.blueprints;
}

export interface NodeLocation {
  parent: Node | null;
  /** Index within the parent's children, or -1 for a non-list slot. */
  index: number;
}

export function newNodeId(prefix = 'n'): string {
  // Not cryptographic: this only needs to be unique inside one document, and a
  // readable prefix makes the constructor's tree far easier to scan.
  return `${prefix}_${Math.random().toString(36).slice(2, 8)}${Date.now().toString(36).slice(-3)}`;
}

export function findNodeIn(root: Node, id: NodeId): Node | undefined {
  if (root.id === id) return root;
  for (const child of directChildren(root)) {
    const found = findNodeIn(child, id);
    if (found) return found;
  }
  return undefined;
}

export function locateNode(root: Node, id: NodeId): NodeLocation | null {
  if (root.id === id) return { parent: null, index: -1 };

  switch (root.kind) {
    case 'stack':
    case 'grid':
    case 'group':
    case 'frame': {
      const index = root.children.findIndex((child) => child.id === id);
      if (index !== -1) return { parent: root, index };
      break;
    }
    case 'overlay': {
      const index = root.children.findIndex((child) => child.node.id === id);
      if (index !== -1) return { parent: root, index };
      break;
    }
    case 'repeater':
      if (root.itemTemplate.id === id || root.empty?.id === id) return { parent: root, index: -1 };
      break;
    case 'slot':
      if (root.defaultChild?.id === id) return { parent: root, index: -1 };
      break;
    default:
      break;
  }

  for (const child of directChildren(root)) {
    const found = locateNode(child, id);
    if (found) return found;
  }
  return null;
}

export function directChildren(node: Node): Node[] {
  switch (node.kind) {
    case 'stack':
    case 'grid':
    case 'group':
    case 'frame':
      return node.children;
    case 'overlay':
      return node.children.map((child) => child.node);
    case 'repeater':
      return node.empty ? [node.itemTemplate, node.empty] : [node.itemTemplate];
    case 'slot':
      return node.defaultChild ? [node.defaultChild] : [];
    default:
      return [];
  }
}

export function acceptsChildren(node: Node): node is ContainerNode {
  return node.kind === 'stack' || node.kind === 'grid' || node.kind === 'group' || node.kind === 'frame';
}

/** Guards against dropping a container into its own subtree. */
export function isDescendant(root: Node, ancestorId: NodeId, candidateId: NodeId): boolean {
  const ancestor = findNodeIn(root, ancestorId);
  if (!ancestor) return false;
  return Boolean(findNodeIn(ancestor, candidateId)) && ancestorId !== candidateId;
}

export function removeNodeFrom(root: Node, id: NodeId): Node | null {
  const location = locateNode(root, id);
  if (!location?.parent) return null;

  const { parent, index } = location;

  if (acceptsChildren(parent) && index >= 0) {
    return parent.children.splice(index, 1)[0] ?? null;
  }
  if (parent.kind === 'overlay' && index >= 0) {
    return parent.children.splice(index, 1)[0]?.node ?? null;
  }
  if (parent.kind === 'slot' && parent.defaultChild?.id === id) {
    const removed = parent.defaultChild;
    parent.defaultChild = undefined;
    return removed;
  }
  if (parent.kind === 'repeater' && parent.empty?.id === id) {
    const removed = parent.empty;
    parent.empty = undefined;
    return removed;
  }
  // A repeater's item template is structural: deleting it would leave the
  // repeater with nothing to clone.
  return null;
}

export function insertNodeInto(root: Node, parentId: NodeId, node: Node, index?: number): boolean {
  const parent = findNodeIn(root, parentId);
  if (!parent) return false;

  if (acceptsChildren(parent)) {
    const at =
      index === undefined ? parent.children.length : clamp(index, 0, parent.children.length);
    parent.children.splice(at, 0, node);
    return true;
  }
  if (parent.kind === 'overlay') {
    const at =
      index === undefined ? parent.children.length : clamp(index, 0, parent.children.length);
    // A fresh overlay child lands in the middle at a quarter size, which is
    // visible immediately and easy to drag somewhere useful.
    parent.children.splice(at, 0, { node, x: 0.375, y: 0.375, width: 0.25, height: 0.25 });
    return true;
  }
  if (parent.kind === 'slot' && !parent.defaultChild) {
    parent.defaultChild = node;
    return true;
  }
  return false;
}

/** Strip Immer drafts before cloning — structuredClone cannot copy proxies. */
export function toPlainValue<T>(value: T): T {
  if (isDraft(value as object)) return current(value as object) as T;
  return value;
}

function clonePlain<T>(value: T): T {
  return JSON.parse(JSON.stringify(toPlainValue(value))) as T;
}

/** Deep copy with every id regenerated, so the clone is independent. */
export function cloneNodeWithNewIds(node: Node): Node {
  const copy = clonePlain(node);
  reassignIds(copy);
  return copy;
}

function reassignIds(node: Node): void {
  node.id = newNodeId(node.kind.slice(0, 3));
  switch (node.kind) {
    case 'stack':
    case 'grid':
    case 'group':
    case 'frame':
      node.children.forEach(reassignIds);
      break;
    case 'overlay':
      node.children.forEach((child) => reassignIds(child.node));
      break;
    case 'repeater':
      reassignIds(node.itemTemplate);
      if (node.empty) reassignIds(node.empty);
      break;
    case 'slot':
      if (node.defaultChild) reassignIds(node.defaultChild);
      break;
    default:
      break;
  }
}

export function duplicateBlueprint(blueprint: PageBlueprint): PageBlueprint {
  const source = toPlainValue(blueprint);
  const variants = source.variant.match(/^(.*?)(\d*)$/);
  const nextVariant = variants
    ? `${variants[1]}${Number(variants[2] || 1) + 1}`
    : `${source.variant} copy`;

  return {
    ...clonePlain(source),
    id: newNodeId('bp'),
    label: `${source.label} copy`,
    variant: nextVariant,
    root: cloneNodeWithNewIds(source.root),
  };
}

/** Every node in a blueprint, flattened, with its depth. Drives the tree panel. */
export function flattenTree(root: Node, depth = 0): Array<{ node: Node; depth: number }> {
  const rows: Array<{ node: Node; depth: number }> = [{ node: root, depth }];
  for (const child of directChildren(root)) {
    rows.push(...flattenTree(child, depth + 1));
  }
  return rows;
}

/** Path from root to a node (inclusive). */
export function buildNodePath(root: Node, id: NodeId): Node[] {
  if (root.id === id) return [root];
  for (const child of directChildren(root)) {
    const path = buildNodePath(child, id);
    if (path.length > 0) return [root, ...path];
  }
  return [];
}

export function describeNode(node: Node): string {
  if (node.name) return node.name;
  switch (node.kind) {
    case 'text':
      return node.source.template.replace(/\{\{|\}\}/g, '').slice(0, 28) || 'Text';
    case 'image':
      return node.source.binding ?? node.source.assetId ?? 'Image';
    case 'repeater':
      if (node.source.includes('items')) return 'Dish list';
      if (node.source.includes('categories')) return 'Category list';
      return 'List';
    case 'slot':
      return `Content hole: ${node.slotId}`;
    case 'grid':
      return node.synthesize ? 'Columns (generated)' : node.columns === 2 ? 'Two columns' : `Columns ${node.columns}`;
    case 'stack':
      return node.direction === 'row' ? 'Row' : 'Column';
    case 'frame': {
      const labels: Record<EntityScope, string> = {
        wrapper: 'Wrapper',
        category: 'Category',
        item: 'Dish',
      };
      return labels[node.entity];
    }
    case 'overlay':
      return 'Decoration';
    default:
      return node.kind[0]!.toUpperCase() + node.kind.slice(1);
  }
}

function clamp(value: number, min: number, max: number): number {
  return value < min ? min : value > max ? max : value;
}

const FALLBACK_SOLID_FILL: Fill = {
  kind: 'solid',
  color: { type: 'literal', hex: '#ffffff' },
};

/** Drop image fills that point at a removed template asset. */
function scrubFillAsset(fill: Fill | undefined, assetId: string): Fill | undefined {
  if (!fill || fill.kind !== 'image' || fill.assetId !== assetId) return fill;
  return FALLBACK_SOLID_FILL;
}

/** Clear every reference to `assetId` so removing an asset never blocks save. */
export function scrubAssetFromDocument(document: TemplateDocument, assetId: string): void {
  const visit = (node: Node): void => {
    if (node.style?.fill) {
      node.style.fill = scrubFillAsset(node.style.fill, assetId)!;
    }
    switch (node.kind) {
      case 'image':
        if (node.source.assetId === assetId) {
          delete node.source.assetId;
        }
        break;
      case 'shape':
        if (node.fill && typeof node.fill !== 'string') {
          node.fill = scrubFillAsset(node.fill, assetId)!;
        }
        break;
      default:
        break;
    }
    for (const child of directChildren(node)) visit(child);
  };

  for (const blueprint of document.blueprints ?? []) visit(blueprint.root);
  for (const page of document.pages ?? []) visit(page.root);
  for (const variant of document.variants ?? []) {
    for (const blueprint of variant.blueprints) visit(blueprint.root);
  }
  for (const preset of document.libraryPresets ?? []) visit(preset.node);
}
